// src/components/DropZone.tsx
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle } from 'lucide-react';
import './DropZone.css';

interface DropZoneProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}

const ALLOWED_EXTENSIONS = ['.exe', '.dll', '.bin', '.com', '.sys'];

const DropZone: React.FC<DropZoneProps> = ({ onFileSelect, disabled }) => {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);

    if (disabled) return;

    // react-dropzone may pass files through acceptedFiles even without
    // MIME filtering — we validate by extension ourselves
    const file = acceptedFiles[0] ?? rejectedFiles[0]?.file;
    if (!file) return;

    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file type "${ext}". Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('File exceeds 50MB limit.');
      return;
    }

    onFileSelect(file);
  }, [onFileSelect, disabled]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    disabled,
    // No accept filter — we handle validation ourselves in onDrop
  });

  return (
    <div
      {...getRootProps()}
      className={`dropzone ${isDragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <input {...getInputProps()} />
      <Upload size={48} className="upload-icon" />
      <p className="dropzone-text">
        {isDragActive ? 'Drop the file here' : 'Drop a binary file or click to browse'}
      </p>
      {error && (
        <div className="dropzone-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default DropZone;